import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useCallback, useState } from 'react';

import type { BuckChatConfig, LtPlusBillingInterval } from '../../../types';
import { ANALYTICS_KEYS, BUCK_EVENTS } from '../../../utils/analytics';

type UseLtPlusCheckoutOptions = {
  buckChatConfig: BuckChatConfig;
  billingInterval: LtPlusBillingInterval;
  onSuccess: () => void;
  onError: (error: string) => void;
  onAnalyticsEvent?: (event: string, data: Record<string, unknown>) => void;
};

type UseLtPlusCheckoutReturn = {
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  clearError: () => void;
};

export const useLtPlusCheckout = ({
  buckChatConfig,
  billingInterval,
  onSuccess,
  onError,
  onAnalyticsEvent,
}: UseLtPlusCheckoutOptions): UseLtPlusCheckoutReturn => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const isSubscriptionExistsError = (err: unknown): boolean => {
    if (!err || typeof err !== 'object') {
      return false;
    }

    const code = 'code' in err ? String(err.code).toLowerCase() : '';
    const type = 'type' in err ? String(err.type).toLowerCase() : '';
    const stripeErrorType =
      'stripeErrorType' in err ? String(err.stripeErrorType).toLowerCase() : '';
    const message = 'message' in err ? String(err.message).toLowerCase() : '';

    return (
      code.includes('subscription_exists') ||
      code.includes('stripe-subscription-exists') ||
      type.includes('subscription_exists') ||
      stripeErrorType.includes('subscription_exists') ||
      message.includes('already') ||
      message.includes('active landtrust+ subscription')
    );
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!stripe || !elements) {
        setError('Payment system not ready. Please try again.');
        return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setError(submitError.message || 'Please check your payment details.');
          setIsSubmitting(false);
          return;
        }

        const createSubscription = buckChatConfig.stripe?.createSubscription;
        if (!createSubscription) {
          throw new Error('Subscription API callback is not configured.');
        }
        const { clientSecret } = await createSubscription({
          billingInterval,
        });

        const { error: confirmError } = await stripe.confirmPayment({
          elements,
          clientSecret,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        });

        if (confirmError) {
          setError(confirmError.message || 'Payment failed. Please try again.');
          onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_CHECKOUT_ERROR, {
            [ANALYTICS_KEYS.BILLING_INTERVAL]: billingInterval,
            [ANALYTICS_KEYS.ERROR_TYPE]: 'payment_confirmation',
            [ANALYTICS_KEYS.ERROR_MESSAGE]: confirmError.message,
          });
          setIsSubmitting(false);
          return;
        }

        onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_CHECKOUT_SUCCESS, {
          [ANALYTICS_KEYS.BILLING_INTERVAL]: billingInterval,
        });
        onSuccess();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';

        setError(errorMessage);
        onError(errorMessage);

        onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_CHECKOUT_ERROR, {
          [ANALYTICS_KEYS.BILLING_INTERVAL]: billingInterval,
          [ANALYTICS_KEYS.ERROR_TYPE]: 'unknown',
          [ANALYTICS_KEYS.ERROR_MESSAGE]: errorMessage,
        });

        if (isSubscriptionExistsError(err)) {
          setError('You already have an active LandTrust+ subscription.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [stripe, elements, billingInterval, buckChatConfig.stripe, onSuccess, onError, onAnalyticsEvent]
  );

  return {
    handleSubmit,
    isSubmitting,
    error,
    clearError,
  };
};
